/**
 * Every permission a tenant role can hold. Labels and hints are shown to admins
 * in the Roles screen, so they are written for someone who has never used a CRM.
 */
export const PERMISSION_GROUPS = [
  {
    key: 'leads',
    label: 'Leads',
    permissions: [
      { key: 'leads.view_own', label: 'See their own leads', hint: 'Leads assigned to this person.' },
      { key: 'leads.view_team', label: "See their team's leads", hint: 'Leads of people who report to them.' },
      { key: 'leads.view_all', label: 'See every lead in the company' },
      { key: 'leads.create', label: 'Add new leads' },
      { key: 'leads.edit', label: 'Edit leads they can see' },
      { key: 'leads.assign', label: 'Assign leads to other people' },
      { key: 'leads.delete', label: 'Delete leads', hint: 'Deleted leads go to Trash and can be restored.' },
      { key: 'leads.import', label: 'Import leads from Excel' },
      { key: 'leads.export', label: 'Download leads to Excel', hint: 'Lets this person take your data out of the CRM.' },
      { key: 'leads.pool', label: 'Take leads from the lead pool', hint: 'Unassigned leads anyone can pick up (when the lead pool is on).' },
    ],
  },
  {
    key: 'team',
    label: 'Team',
    permissions: [
      { key: 'users.view', label: 'See the team list' },
      { key: 'users.manage', label: 'Add, edit and deactivate team members' },
      { key: 'roles.manage', label: 'Change what each role can do' },
      { key: 'attendance.view_team', label: 'See the team’s attendance', hint: 'Everyone they can see leads for.' },
      { key: 'tracking.view_team', label: 'See where the team is (live map)', hint: 'Only while people are on duty with the app.' },
    ],
  },
  {
    key: 'sales',
    label: 'Quotations & invoices',
    permissions: [
      { key: 'documents.manage', label: 'Make quotations and invoices', hint: 'For leads and customers they can see.' },
      { key: 'documents.view_all', label: 'See every quotation and invoice in the company' },
      { key: 'payments.record', label: 'Record payments received' },
      { key: 'products.manage', label: 'Edit products and prices' },
      { key: 'targets.manage', label: 'Set sales targets' },
      { key: 'inventory.manage', label: 'Add and edit inventory (flats, plots…)', hint: 'Everyone can see what’s available and hold a unit for their lead.' },
    ],
  },
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    permissions: [
      { key: 'whatsapp.chat', label: 'Chat with customers on WhatsApp', hint: 'Conversations of leads they can see.' },
      { key: 'whatsapp.campaigns', label: 'Send WhatsApp campaigns to many leads' },
      { key: 'webchat.chat', label: 'Answer chats from the website', hint: 'Visitors who chat on your website.' },
    ],
  },
  {
    key: 'ai',
    label: 'AI',
    permissions: [
      { key: 'ai.use', label: 'Use AI helpers', hint: 'Ask AI, AI notes and lead advice. Uses your AI credits.' },
    ],
  },
  {
    key: 'hr',
    label: 'Tasks & HR',
    permissions: [
      { key: 'tasks.team', label: 'Give tasks to their team and approve the team’s leaves and expenses', hint: 'For people who report to them.' },
      { key: 'hr.manage', label: 'Manage salaries, bank details, all leaves and expenses' },
    ],
  },
  {
    key: 'reports',
    label: 'Reports',
    permissions: [
      { key: 'reports.view_own', label: 'See their own reports' },
      { key: 'reports.view_all', label: 'See reports for the whole company' },
    ],
  },
  {
    key: 'settings',
    label: 'Settings',
    permissions: [
      { key: 'settings.company', label: 'Change company profile and branding' },
      { key: 'settings.manage', label: 'Change lead setup, integrations and other settings' },
      { key: 'billing.manage', label: 'See plan, pay bills and buy credits' },
      { key: 'audit.view', label: 'See the activity log' },
    ],
  },
] as const;

export type Permission = (typeof PERMISSION_GROUPS)[number]['permissions'][number]['key'];

export const ALL_PERMISSIONS: Permission[] = PERMISSION_GROUPS.flatMap((g) => g.permissions.map((p) => p.key));

export function isPermission(value: string): value is Permission {
  return (ALL_PERMISSIONS as string[]).includes(value);
}

export type SystemRoleKey = 'admin' | 'manager' | 'team_leader' | 'staff';

/** Roles every new company starts with. Admin always has everything and cannot be edited. */
export const SYSTEM_ROLES: { key: SystemRoleKey; name: string; description: string; permissions: Permission[] }[] = [
  {
    key: 'admin',
    name: 'Admin',
    description: 'Full access to everything, including settings and billing.',
    permissions: ALL_PERMISSIONS,
  },
  {
    key: 'manager',
    name: 'Manager',
    description: 'Runs the sales team. Sees all leads and reports, manages people.',
    permissions: [
      'leads.view_own', 'leads.view_team', 'leads.view_all', 'leads.create', 'leads.edit', 'leads.assign',
      'leads.import', 'leads.export', 'users.view', 'users.manage', 'reports.view_own', 'reports.view_all',
      'attendance.view_team', 'tracking.view_team', 'whatsapp.chat', 'whatsapp.campaigns',
      'documents.manage', 'documents.view_all', 'payments.record', 'products.manage', 'targets.manage', 'inventory.manage',
      'webchat.chat', 'ai.use', 'leads.pool', 'tasks.team',
    ],
  },
  {
    key: 'team_leader',
    name: 'Team Leader',
    description: 'Leads a small group. Sees and assigns their team’s leads.',
    permissions: ['leads.view_own', 'leads.view_team', 'leads.create', 'leads.edit', 'leads.assign', 'users.view', 'reports.view_own', 'attendance.view_team', 'whatsapp.chat', 'documents.manage', 'ai.use', 'leads.pool', 'tasks.team'],
  },
  {
    key: 'staff',
    name: 'Sales Staff',
    description: 'Calls and follows up on the leads given to them.',
    permissions: ['leads.view_own', 'leads.create', 'leads.edit', 'reports.view_own', 'whatsapp.chat', 'documents.manage', 'ai.use', 'leads.pool'],
  },
];
